import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Moment } from 'moment';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  Table,
  Typography,
} from 'antd';
// Api
import { GET_TICKET_EVENT_FORM_DEFAULTS } from 'api/ticketing/queries';
// Types
import { GetTicketEventFormDefaults } from 'api/ticketing/types/GetTicketEventFormDefaults';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketTierFields } from 'api/ticketing/types/TicketTierFields';
import { TicketEventStatus, TicketTaxMode } from 'api/graphql-global-types';
import {
  PublishMode,
  TicketEventFormMode,
  TicketEventFormValues,
  VenueMode,
} from './types';
// Hooks
import { useGetSports } from 'hooks/useGetSports';
import { useGetLeagues } from 'hooks/useGetLeagues';
// Helpers
import {
  formatCount,
  formatMoney,
  formatVenueAddress,
  futureInVenueRule,
  getAddTierAvailability,
  getAffiliateEarningsRange,
  getCommissionRules,
  getTicketPriceLimits,
  getTimezoneLabel,
  isEventEditableState,
  REPEAT_LABELS,
  venueDateTimePickerProps,
} from 'pages/Events/helpers';
import {
  getMatchcardError,
  mapBoutsToMatchupsInput,
} from 'pages/Events/matchcard';
import {
  formatHours,
  getShiftedSchedule,
  getTierCapacityTotal,
  isVenueLocked,
} from './helpers';
// Constants
// UI
import { errorNotification } from 'ui/Notification';
// Components
import HostSelect from './components/HostSelect/HostSelect';
import VenueSection, {
  SavedVenue,
} from './components/VenueSection/VenueSection';
import TiersEditor from './components/TiersEditor/TiersEditor';
import CoverImageInput from './components/CoverImageInput/CoverImageInput';
import EventSummary from './components/EventSummary/EventSummary';
import StreamSelect from './components/StreamSelect/StreamSelect';
import MatchcardFields from 'pages/Events/components/MatchcardFields/MatchcardFields';
// Styles
import styles from './TicketEventForm.module.scss';

const { Text, Paragraph } = Typography;

export const EVENT_NAME_MAX_LENGTH = 250;
export const EVENT_DESCRIPTION_MAX_LENGTH = 5000;
export const MAX_HASHTAGS = 10;
export const MAX_SPORTS = 10;

const REPEAT_OPTIONS = [
  { value: '', label: "Doesn't repeat" },
  ...Object.entries(REPEAT_LABELS).map(([value, label]) => ({ value, label })),
];

export type TicketEventFormSubmit = {
  values: TicketEventFormValues;
  timezone: string;
  coverImage: File | null;
};

type TicketEventFormProps = {
  mode: TicketEventFormMode;
  initialValues: TicketEventFormValues;
  event?: TicketEventDetailsFields | null;
  savedTiers?: TicketTierFields[];
  submitting: boolean;
  onSubmit: (submit: TicketEventFormSubmit) => void;
  onCancel: () => void;
};

const TicketEventForm = ({
  mode,
  initialValues,
  event,
  savedTiers = [],
  submitting,
  onSubmit,
  onCancel,
}: TicketEventFormProps): JSX.Element => {
  const [form] = Form.useForm<TicketEventFormValues>();
  const [watched, setWatched] = useState<TicketEventFormValues>(initialValues);
  const [savedVenues, setSavedVenues] = useState<SavedVenue[]>([]);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const isEdit = mode === 'edit';
  const venueLocked = isEdit && isVenueLocked(event?.status);

  const { data: defaultsData } = useQuery<GetTicketEventFormDefaults>(
    GET_TICKET_EVENT_FORM_DEFAULTS,
    { fetchPolicy: 'cache-and-network' }
  );
  const { data: sportsData, loading: sportsLoading } = useGetSports();
  const { data: leaguesData, loading: leaguesLoading } = useGetLeagues();

  const setting = defaultsData?.getTicketingSetting;
  const scheduleDefaults = defaultsData?.ticketEventScheduleDefaults;
  const { minTicketPrice, maxTicketPrice } = getTicketPriceLimits(setting);

  const timezone = useMemo((): string | undefined => {
    if (venueLocked) {
      return event?.timezone;
    }

    if (watched.venueMode === VenueMode.Saved) {
      return (
        savedVenues.find((venue) => venue.id === watched.venueId)?.timezone ||
        (watched.venueId === event?.venueId ? event?.timezone : undefined)
      );
    }

    return watched.venue?.timezone;
  }, [venueLocked, watched, savedVenues, event]);

  const tierAvailability =
    isEdit && event ? getAddTierAvailability(event.status) : null;
  const canAddTiers = !isEdit || !!tierAvailability?.allowed;
  const tierChangePausesSales = !!tierAvailability?.pausesSales;
  const canChangeTicketsEnabled =
    !isEdit || (!!event && isEventEditableState(event.status));

  const linkedToStream = Boolean(watched.streamId || event?.streamId);
  const hasBouts = isEdit
    ? Boolean(event?.matchups.length)
    : mapBoutsToMatchupsInput(watched.bouts).length > 0;
  const ticketPrices = [...savedTiers, ...(watched.tiers || [])]
    .map((tier) => Number(tier.price) || 0)
    .filter((price) => price > 0);
  const affiliateEarnings =
    watched.affiliateEnabled && !linkedToStream
      ? getAffiliateEarningsRange(
          ticketPrices,
          watched.affiliateCommissionPercent
        )
      : null;

  const outcome = isEdit
    ? 'Changes are saved; the status stays as it is'
    : watched.publishMode === PublishMode.Publish
    ? 'On sale right away'
    : 'Draft, hidden from fans';

  const handleFinish = (values: TicketEventFormValues): void => {
    if (!timezone) {
      errorNotification('Pick a venue with a time zone first.');
      return;
    }

    const cardError = linkedToStream
      ? undefined
      : getMatchcardError(values.bouts);

    if (cardError) {
      errorNotification(cardError);
      return;
    }

    onSubmit({ values, timezone, coverImage });
  };

  const syncWatched = (): void =>
    setWatched({ ...initialValues, ...form.getFieldsValue(true) });

  const previousStart = useRef<Moment | null | undefined>(
    initialValues.startsAt
  );

  // Moving the start drags the other times along by the same amount
  const handleValuesChange = (
    changed: Partial<TicketEventFormValues>,
    all: TicketEventFormValues
  ): void => {
    let next = all;

    if (changed.startsAt && previousStart.current) {
      const shifted = getShiftedSchedule(
        all,
        changed.startsAt.diff(previousStart.current)
      );
      form.setFieldsValue(shifted);
      next = { ...all, ...shifted };
    }

    if ('startsAt' in changed) {
      previousStart.current = changed.startsAt;
    }

    // A stream belongs to its host
    if ('hostId' in changed && all.streamId) {
      form.setFieldsValue({ streamId: null });
      next = { ...next, streamId: null };
    }

    setWatched({ ...initialValues, ...next });
  };

  const requiredBefore =
    (field: keyof TicketEventFormValues, label: string) =>
    ({ getFieldValue }: { getFieldValue: (name: string) => Moment }) => ({
      validator: (_: unknown, value?: Moment | null) => {
        const other = getFieldValue(field);

        return !value || !other || value.isBefore(other)
          ? Promise.resolve()
          : Promise.reject(new Error(`Must be before ${label}`));
      },
    });

  const requiredAfter =
    (field: keyof TicketEventFormValues, label: string) =>
    ({ getFieldValue }: { getFieldValue: (name: string) => Moment }) => ({
      validator: (_: unknown, value?: Moment | null) => {
        const other = getFieldValue(field);

        return !value || !other || value.isAfter(other)
          ? Promise.resolve()
          : Promise.reject(new Error(`Must be after ${label}`));
      },
    });

  return (
    <Form<TicketEventFormValues>
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={handleValuesChange}
      onFinish={handleFinish}
      onFinishFailed={() =>
        errorNotification('Some fields need attention. They are marked in red.')
      }
      scrollToFirstError
      requiredMark="optional"
      className={styles.root}
    >
      <Row gutter={24}>
        <Col xs={24} xl={17} className={styles.sections}>
          {isEdit && event?.status === TicketEventStatus.Cancelled && (
            <Alert
              type="error"
              showIcon
              message="This event is cancelled and can't be changed."
            />
          )}

          <Card title="Host and details" className={styles.card}>
            <Row gutter={24}>
              <Col xs={24} lg={15}>
                <Form.Item
                  name="hostId"
                  label="Host"
                  extra={
                    isEdit
                      ? "An event's host can't change."
                      : 'The store that runs the event and is paid for its tickets.'
                  }
                  rules={[{ required: true, message: 'Choose the host' }]}
                >
                  <HostSelect disabled={isEdit} />
                </Form.Item>
                {!isEdit && (
                  <Form.Item
                    name="streamId"
                    label="Stream"
                    extra="Sell in-person tickets for one of the host's scheduled streams. The event takes the stream's name, start, fight card and affiliate program unless you set them here."
                  >
                    <StreamSelect hostId={watched.hostId} />
                  </Form.Item>
                )}
                {isEdit && event?.stream && (
                  <Form.Item label="Stream">
                    <Input value={event.stream.name || ''} disabled />
                  </Form.Item>
                )}
                <Form.Item
                  name="name"
                  label="Event name"
                  rules={[
                    {
                      required: !linkedToStream,
                      whitespace: true,
                      message: 'Name the event',
                    },
                    {
                      max: EVENT_NAME_MAX_LENGTH,
                      message: `At most ${EVENT_NAME_MAX_LENGTH} characters`,
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      linkedToStream
                        ? "The stream's name"
                        : 'e.g. Championship Fight Night'
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    {
                      max: EVENT_DESCRIPTION_MAX_LENGTH,
                      message: `At most ${EVENT_DESCRIPTION_MAX_LENGTH} characters`,
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={5}
                    showCount
                    maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
                    placeholder="Tell people what to expect from the event."
                  />
                </Form.Item>
                <Form.Item
                  name="hashtags"
                  label="Tags"
                  rules={[
                    {
                      type: 'array',
                      max: MAX_HASHTAGS,
                      message: `At most ${MAX_HASHTAGS} tags`,
                    },
                  ]}
                >
                  <Select
                    mode="tags"
                    placeholder="Type a tag and press Enter"
                    tokenSeparators={[',']}
                    open={false}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={9}>
                <Form.Item label="Cover">
                  <CoverImageInput
                    file={coverImage}
                    onChange={setCoverImage}
                    currentImageUrl={event?.coverImageUrl}
                    onError={(message) => errorNotification(message)}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Venue" className={styles.card}>
            <VenueSection
              form={form}
              hostId={watched.hostId}
              venueMode={watched.venueMode}
              locked={venueLocked}
              lockedVenue={
                event
                  ? {
                      name: event.venueName || 'Venue',
                      address: event.venueAddress
                        ? formatVenueAddress(event.venueAddress)
                        : '',
                      timezone: event.timezone,
                    }
                  : null
              }
              onVenuesLoaded={setSavedVenues}
              onFieldsFilled={syncWatched}
            />
          </Card>

          <Card title="Schedule" className={styles.card}>
            {timezone ? (
              <Paragraph type="secondary">
                Times are in the venue&apos;s time zone:{' '}
                <Text strong>{getTimezoneLabel(timezone)}</Text>.
              </Paragraph>
            ) : (
              <Alert
                className={styles.inlineAlert}
                type="info"
                showIcon
                message="Pick the venue first: times are read in its time zone."
              />
            )}
            {isEdit && (
              <Paragraph type="secondary">
                Changing a date reschedules the event: ticket holders are
                notified, sales windows move by the same amount and a published
                event comes back paused.
              </Paragraph>
            )}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="startsAt"
                  label="Starts"
                  rules={[
                    {
                      required: !linkedToStream,
                      message: 'Set the start time',
                    },
                    // An edit may keep a start that has passed
                    ...(isEdit
                      ? []
                      : [
                          futureInVenueRule(
                            timezone,
                            'The start must be in the future'
                          ),
                        ]),
                  ]}
                >
                  <DatePicker
                    {...venueDateTimePickerProps(timezone)}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="endsAt"
                  label="Ends"
                  dependencies={['startsAt']}
                  extra={
                    !isEdit && scheduleDefaults
                      ? `Defaults to ${formatHours(
                          scheduleDefaults.eventDurationSeconds
                        )}.`
                      : undefined
                  }
                  rules={[
                    { required: isEdit, message: 'Set the end time' },
                    requiredAfter('startsAt', 'the start'),
                  ]}
                >
                  <DatePicker
                    {...venueDateTimePickerProps(timezone)}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="doorsOpenAt"
                  label="Doors open"
                  dependencies={['startsAt']}
                  extra={
                    !isEdit ? 'Defaults to 1 hour before the start.' : undefined
                  }
                  rules={[
                    { required: isEdit, message: 'Set when doors open' },
                    ({ getFieldValue }) => ({
                      validator: (_, value?: Moment | null) => {
                        const start = getFieldValue('startsAt');

                        return !value || !start || !value.isAfter(start)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error('Doors must open by the start')
                            );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    {...venueDateTimePickerProps(timezone)}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="admissionClosesAt"
                  label="Admission closes"
                  dependencies={['doorsOpenAt']}
                  extra={!isEdit ? 'Defaults to the end time.' : undefined}
                  rules={[
                    { required: isEdit, message: 'Set when admission closes' },
                    requiredAfter('doorsOpenAt', 'doors open'),
                  ]}
                >
                  <DatePicker
                    {...venueDateTimePickerProps(timezone)}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="salesOpenAt"
                  label="Sales open"
                  dependencies={['salesCloseAt']}
                  extra={
                    !isEdit
                      ? 'Defaults to when the event goes on sale.'
                      : undefined
                  }
                  rules={[requiredBefore('salesCloseAt', 'sales close')]}
                >
                  <DatePicker
                    {...venueDateTimePickerProps(timezone)}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="salesCloseAt"
                  label="Sales close"
                  dependencies={['startsAt']}
                  extra={
                    scheduleDefaults
                      ? `${
                          isEdit
                            ? ''
                            : `Defaults to ${formatHours(
                                scheduleDefaults.salesCloseAfterStartSeconds
                              )}. `
                        }Latest allowed: ${formatHours(
                          scheduleDefaults.maxSalesCloseAfterStartSeconds
                        )}.`
                      : undefined
                  }
                  rules={[
                    { required: isEdit, message: 'Set when sales close' },
                    ({ getFieldValue }) => ({
                      validator: (_, value?: Moment | null) => {
                        const start: Moment | undefined =
                          getFieldValue('startsAt');
                        const maxAfter =
                          scheduleDefaults?.maxSalesCloseAfterStartSeconds;

                        return !value ||
                          !start ||
                          maxAfter === undefined ||
                          !value.isAfter(start.clone().add(maxAfter, 's'))
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                `Sales must close by ${formatHours(maxAfter)}`
                              )
                            );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    {...venueDateTimePickerProps(timezone)}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              {!linkedToStream && (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="repeatsEvery"
                    label="Repeats"
                    normalize={(value?: string) => value || null}
                  >
                    <Select
                      options={REPEAT_OPTIONS}
                      placeholder="Doesn't repeat"
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Card>

          <Card title="Tickets" className={styles.card}>
            <div className={styles.switchRow}>
              <div>
                <Text strong>Sell tickets</Text>
                <Paragraph type="secondary" className={styles.switchHint}>
                  Off lists the event without tickets, for a free or stream-only
                  event.
                </Paragraph>
              </div>
              <Form.Item name="ticketsEnabled" valuePropName="checked" noStyle>
                <Switch
                  aria-label="Sell tickets"
                  disabled={!canChangeTicketsEnabled}
                />
              </Form.Item>
            </div>

            {watched.ticketsEnabled && (
              <>
                {isEdit && savedTiers.length > 0 && (
                  <Table<TicketTierFields>
                    className={styles.savedTiers}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={savedTiers}
                    columns={[
                      { title: 'Tier', dataIndex: 'label', key: 'label' },
                      {
                        title: 'Price',
                        key: 'price',
                        render: (_, tier) => formatMoney(tier.price),
                      },
                      {
                        title: 'Quantity',
                        key: 'capacity',
                        render: (_, tier) => formatCount(tier.capacity),
                      },
                      {
                        title: 'Max per order',
                        dataIndex: 'maxPerOrder',
                        key: 'maxPerOrder',
                      },
                    ]}
                  />
                )}

                {isEdit && (
                  <Paragraph type="secondary">
                    Saved tiers can&apos;t be edited or removed.{' '}
                    {canAddTiers
                      ? tierChangePausesSales
                        ? 'New tiers are added while sales pause for a moment; they reopen right after.'
                        : 'You can add new tiers below.'
                      : tierAvailability?.reason ||
                        'Tiers can no longer be added to this event.'}
                  </Paragraph>
                )}

                {canAddTiers && (
                  <TiersEditor
                    minTicketPrice={minTicketPrice}
                    maxTicketPrice={maxTicketPrice}
                    savedTierCount={savedTiers.length}
                    requireOne={!isEdit}
                    addLabel={isEdit ? 'Add a new tier' : 'Add tier'}
                  />
                )}

                <Form.Item
                  name="maxAdmissions"
                  label="Venue capacity"
                  className={styles.capacity}
                  dependencies={['tiers']}
                  extra="Optional. Caps all tiers together; defaults to their total."
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: (_, value?: number | null) => {
                        const total =
                          getTierCapacityTotal(getFieldValue('tiers')) +
                          getTierCapacityTotal(savedTiers);

                        // An untouched capacity is raised on save to fit new tiers
                        const untouched =
                          isEdit && value === event?.maxAdmissions;

                        return !value || value >= total || untouched
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                `At least the tiers' total (${formatCount(
                                  total
                                )})`
                              )
                            );
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={1000000}
                    precision={0}
                    className={styles.picker}
                  />
                </Form.Item>
              </>
            )}
          </Card>

          <Card title="Event info" className={styles.card}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="promotion"
                  label="Promotion"
                  rules={[{ max: 120, message: 'At most 120 characters' }]}
                >
                  <Input placeholder="The promotion or organizer brand" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="minimumAge"
                  label="Minimum age"
                  extra="Leave empty for all ages."
                  rules={[
                    {
                      type: 'integer',
                      min: 1,
                      max: 99,
                      message: 'From 1 to 99',
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={99}
                    precision={0}
                    className={styles.picker}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sportIds"
                  label="Sports"
                  rules={[
                    {
                      type: 'array',
                      max: MAX_SPORTS,
                      message: `At most ${MAX_SPORTS} sports`,
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    loading={sportsLoading}
                    optionFilterProp="label"
                    placeholder="Choose sports"
                    options={(sportsData?.sports || []).map((sport) => ({
                      value: sport.id,
                      label: sport.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="leagueId" label="League">
                  <Select
                    allowClear
                    showSearch
                    loading={leaguesLoading}
                    optionFilterProp="label"
                    placeholder="Choose a league"
                    options={(leaguesData?.getLeaguesList || []).map(
                      (league) => ({ value: league.id, label: league.name })
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="entryRequirements"
              label="Entry requirements"
              rules={[{ max: 5000, message: 'At most 5,000 characters' }]}
            >
              <Input.TextArea
                rows={3}
                placeholder="ID, dress code, bag policy…"
              />
            </Form.Item>
            <Form.Item
              name="refundPolicy"
              label="Refund policy"
              rules={[{ max: 10000, message: 'At most 10,000 characters' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item
              name="termsAndConditions"
              label="Terms and conditions"
              rules={[{ max: 20000, message: 'At most 20,000 characters' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Card>

          {!isEdit && !linkedToStream && (
            <Card title="Matchcard" className={styles.card}>
              <Paragraph type="secondary">
                Optional. Bouts show on the event page; registered fighters join
                as participants and invited ones get a link to claim their spot.
              </Paragraph>
              <MatchcardFields form={form} />
            </Card>
          )}

          {isEdit && !linkedToStream && (
            <Alert
              type="info"
              showIcon
              message="The matchcard is edited on the event page, in the Matchcard tab."
            />
          )}

          {!linkedToStream && (
            <Card title="Affiliates and fan engagement" className={styles.card}>
              <div className={styles.switchRow}>
                <div>
                  <Text strong>Affiliate program</Text>
                  <Paragraph type="secondary" className={styles.switchHint}>
                    Participants earn a share of each ticket sold through their
                    own link. One rate applies to everyone.
                  </Paragraph>
                </div>
                <Form.Item
                  name="affiliateEnabled"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch aria-label="Affiliate program" />
                </Form.Item>
              </div>
              {watched.affiliateEnabled && (
                <Form.Item
                  name="affiliateCommissionPercent"
                  label="Commission (%)"
                  className={styles.capacity}
                  extra={
                    affiliateEarnings
                      ? `Participant earns ${affiliateEarnings} per ticket sale.`
                      : undefined
                  }
                  rules={getCommissionRules(true)}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    className={styles.picker}
                  />
                </Form.Item>
              )}
              <div className={styles.switchRow}>
                <div>
                  <Text strong>Fan polling</Text>
                  <Paragraph type="secondary" className={styles.switchHint}>
                    {hasBouts
                      ? 'Fans vote on who wins each bout on the event page.'
                      : 'Needs at least one bout on the matchcard.'}
                  </Paragraph>
                </div>
                <Form.Item
                  name="fanPollingEnabled"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch aria-label="Fan polling" disabled={!hasBouts} />
                </Form.Item>
              </div>
            </Card>
          )}

          {!isEdit && (
            <Card title="Publishing" className={styles.card}>
              <Form.Item name="publishMode">
                <Radio.Group>
                  <Radio value={PublishMode.Draft}>
                    Save as a draft. Fans can&apos;t see it until it&apos;s
                    published from the event page.
                  </Radio>
                  <Radio value={PublishMode.Publish}>
                    Publish now and open ticket sales.
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                name="approveZeroTax"
                valuePropName="checked"
                dependencies={['publishMode']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, checked: boolean) =>
                      checked ||
                      getFieldValue('publishMode') !== PublishMode.Publish
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              'Publishing needs the zero-tax policy approved'
                            )
                          ),
                  }),
                ]}
                extra="Sales can't open until an event has a tax policy. Ticket prices are charged with no sales tax added."
              >
                <Checkbox>Approve the zero-tax policy for this event</Checkbox>
              </Form.Item>
            </Card>
          )}

          {isEdit && event?.taxMode === TicketTaxMode.Unconfigured && (
            <Alert
              type="info"
              showIcon
              message="This event's tax policy isn't approved yet. Approve it from the event page before opening sales."
            />
          )}
        </Col>

        <Col xs={24} xl={7}>
          <EventSummary
            ticketsEnabled={watched.ticketsEnabled}
            tiers={watched.tiers || []}
            savedTiers={savedTiers}
            maxAdmissions={watched.maxAdmissions}
            timezone={timezone}
            serviceFeePercent={setting?.serviceFeePercent}
            serviceFeePerTicket={setting?.serviceFeePerTicket}
            affiliatePercent={
              watched.affiliateEnabled && !linkedToStream
                ? watched.affiliateCommissionPercent
                : null
            }
            affiliateEarnings={affiliateEarnings}
            outcome={outcome}
          />
        </Col>
      </Row>

      <div className={styles.footer}>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={event?.status === TicketEventStatus.Cancelled}
        >
          {isEdit
            ? 'Save changes'
            : watched.publishMode === PublishMode.Publish
            ? 'Create and publish'
            : 'Create draft'}
        </Button>
      </div>
    </Form>
  );
};

export default TicketEventForm;
